import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { useAccounts } from './auth/store';
import { mailboxPath, sectionPath } from './mail/urls';
import Login from './views/Login';
import CalendarView from './views/Calendar';
import Contacts from './views/Contacts';
import MailboxView from './views/Mailbox';
import Settings from './views/Settings';

function Root() {
    const accounts = useAccounts((s) => s.accounts);
    const checking = useAccounts((s) => s.checking);
    // Nothing at all until the server has answered: showing the login form first and replacing
    // it a moment later makes a signed-in reload look like being signed out.
    if (checking) return null;
    if (!accounts.length) return <Login />;
    return <Navigate to={mailboxPath(accounts[0]!.id)} replace />;
}

/*
    /settings was bookmarkable for a while and a link that used to work should keep working. It
    points at the same screen under the signed-in mailbox, which is where it lives now that it
    renders inside the mail shell rather than replacing it.
*/
function Redirect({ to }: { to: 'settings' }) {
    const accounts = useAccounts((s) => s.accounts);
    const activeId = useAccounts((s) => s.activeId);
    // Carried across, not dropped: ?tab=rules is how "make a rule from this
    // message" lands on the right tab, and an old bookmark still holds it.
    const { search } = useLocation();
    const account = accounts.find((a) => a.id === activeId) ?? accounts[0];
    if (!account) return <Login />;
    return <Navigate to={`${sectionPath(account.id, to)}${search}`} replace />;
}

export default function App() {
    /*
        Ask the server who is signed in, here rather than inside the mailbox view, so that
        landing directly on /settings works the same as landing on the inbox.
    */
    const bootstrap = useAccounts((s) => s.bootstrap);
    useEffect(() => {
        void bootstrap();
    }, [bootstrap]);

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<Root />} />
                <Route path="/login" element={<Login />} />
                <Route path="/m/:acct" element={<MailboxView />} />
                {/* Static segments are matched before :mbox, so these win. */}
                <Route path="/m/:acct/contacts" element={<Contacts />} />
                <Route path="/m/:acct/calendar" element={<CalendarView />} />
                <Route path="/m/:acct/settings" element={<Settings />} />
                {/* Folders have a segment of their own so that one called `contacts` or
                    `settings` can be opened at all — see src/mail/urls.ts. */}
                <Route path="/m/:acct/f/:mbox" element={<MailboxView />} />
                <Route path="/m/:acct/f/:mbox/:email" element={<MailboxView />} />
                {/* The old shape, kept because it was bookmarkable. Nothing generates it now. */}
                <Route path="/m/:acct/:mbox" element={<MailboxView />} />
                <Route path="/m/:acct/:mbox/:email" element={<MailboxView />} />
                <Route path="/settings" element={<Redirect to="settings" />} />
                {/* The old debug screen was a dead end; Settings › Accounts replaced it. */}
                <Route path="/accounts" element={<Redirect to="settings" />} />
                <Route path="*" element={<Root />} />
            </Routes>
        </BrowserRouter>
    );
}
